import type { Scene } from '@/types';

/**
 * Los seis finales — rework ceremonial.
 *
 * Cada final tiene 2-3 escenas con prosa densa. Todos respetan el estado
 * acumulado: Pipo vivo o perdido, Urro muerto, Cuervo-recipiente o
 * Cuervo-sobreviviente, Sena al sur o con Tebo, etc.
 */
export const ENDING_ESCENAS: Scene[] = [
  // ===========================================================================
  //  1. EL GUARDIÁN ETERNO
  // ===========================================================================
  {
    id: 'ending_guardian_1',
    capitulo: 5,
    titulo: 'El Guardián Eterno',
    background: { type: 'mystical', hint: 'plaza del Corazón con todas las facciones alrededor de Pipo' },
    musica: 'ending_good',
    texto: [
      'Caminás. Lo hacés despacio, porque si lo hacés rápido el mundo se te rompe en la primera baldosa. Cruzás la plaza entre balas que parecen tener decidido no tocarte. Llegás a Pipo. Le ponés la mano en la frente, que le arde.',
      'Le hablás sin abrir la boca. Le decís que lo vas a quedar con él. Que no vas a dejar que lo use nadie más, ni siquiera vos. Que vos venías cargando la culpa de haberte ido de tu casa a los dieciocho años sin despedirte, y esa culpa la [emph]donaste a una piedra[/emph] hace tres días. Y que esa donación te dejó un hueco que hoy vas a llenar con él. Con Pipo entero, no con la sombra que quiere meterse adentro.',
      'La cosa que quería entrar en Pipo duda. Los recipientes calibrados eran dos: él, y vos, como vaso humano complementario. Un dato que ni el Cuervo sabía. Lo descubrís en el momento. Te parás enfrente. La cosa duda más. La ves como una humedad en el aire: intenta un cuerpo, intenta otro, intenta moverse sin recipiente y no puede. Se desarma. No se muere: se [glow color=mystic]dispersa[/glow]. Vuelve a lo que era antes. Polvo de algo antiguo.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'ending_guardian_eterno', valor: true },
      { kind: 'xp', cantidad: 200 },
    ],
    opciones: [
      {
        id: 'eg_avanzar',
        texto: 'Alzar a Pipo. Mirar al mundo de ustedes.',
        destino: 'ending_guardian_2',
        intencion: 'empatico',
      },
    ],
  },
  {
    id: 'ending_guardian_2',
    capitulo: 5,
    background: { type: 'mystical', hint: 'Reyes rodeado, Franco con Pipo en brazos' },
    texto: [
      'Cuando mirás, la batalla se detuvo. Nadie supo explicártelo nunca. Los hombres de Reyes están parados con los rifles bajos, como si les hubieran tocado el hombro. Reyes mismo te mira con una confusión que vos no le habías visto a ningún hombre con apellido: la confusión de entender que dejó de mandar.',
      'Orón —si vino— lo tira al suelo con un golpe de mango en la nuca. No lo mata. Acepta su rendición sin palabras. Los otros de Reyes tiran las armas.',
      'Vega se acerca. Te pone la palma, por primera vez, efectivamente sobre la frente. La siente la piel. Lo que te dice no lo escuchás con los oídos: lo escuchás con el hueco que la piedra te dejó hace tres días. —Bien hecho, hijo. Quedate. Te vamos a enseñar a cuidarlo.',
      'Náyade asiente. Los disidentes de La Sombra [if flag=sena_al_sur]bajan la vista y vuelven al sur con una rama de árbol roja como señal de cese definitivo[/if][if noflag=sena_al_sur]—si vinieron— caminan hacia vos como se camina hacia un templo nuevo—.',
      'Pipo se te duerme en el brazo, con los ojos devueltos a sí mismos, más cansado que vos.',
    ],
    opciones: [
      {
        id: 'eg_epilogo',
        texto: 'Dejar que la noche cierre el día.',
        destino: 'ending_guardian_3',
        intencion: 'neutral',
      },
    ],
  },
  {
    id: 'ending_guardian_3',
    capitulo: 5,
    titulo: 'Epílogo — El Guardián Eterno',
    background: { type: 'village', hint: 'villa próspera años después, Franco viejo' },
    texto: [
      'Pasan años. La villa se reconstruye despacio. El bosque le come lo perdido al Corazón y del Corazón queda, al fin, un monumento vegetal más bello que el original. La enfermedad del río se cura con un acuerdo viejo entre los Susurradores y una tribu humana que no sabía que existía la tribu capibara: un acuerdo que [if flag=pacto_rescate_rami]Rami negoció caminando un año entero por el sur[/if][if noflag=pacto_rescate_rami]fue forjado por una mujer joven, del sur, que nunca quiso dar su nombre[/if].',
      'Franco se queda. No en la villa —eso hubiera sido mucho pedirle—, en una cabaña nueva al borde del Bosque Susurrante, no muy distinta de la que le quemaron. Pipo se queda con él. Ningún carpincho sapientis dura más de treinta años. Pipo dura cincuenta y dos.',
      'Cuando Franco muere, no es de viejo: es con las manos limpias. Se sienta en la hamaca al final del día, deja los brazos colgados, y eso es todo. [glow color=moss]Pipo lo vela.[/glow] Los carpinchos que vinieron a cuidarlo al final aprenden el idioma humano por primera vez en la historia reciente, para poder decir al viento unas palabras que ningún animal debería tener que saber.',
      '[whisper]Final: El Guardián Eterno. El tejido se repara despacio.[/whisper]',
    ],
    final: 'guardian_eterno',
    opciones: [
      {
        id: 'eg_volver_menu',
        texto: 'Volver al menú.',
        destino: 'cap1_intro',
        intencion: 'neutral',
      },
    ],
  },

  // ===========================================================================
  //  2. RENACIMIENTO
  // ===========================================================================
  {
    id: 'ending_renacimiento_1',
    capitulo: 5,
    titulo: 'Renacimiento',
    background: { type: 'village', hint: 'villa con banderas a media asta, amanecer frío' },
    musica: 'ending_neutral',
    texto: [
      'Elegiste lo posible, no lo imposible. La batalla sigue. La ganan, pero con precio. Reyes se rinde al tercer carro de pólvora volado por los Guardianes: no estaba dispuesto a morir por su trabajo y eso, al fin, es lo que le salva la vida.',
      'La piedra [if flag=cuervo_contenedor]sigue con el Cuervo, dormida[/if][if flag=ritual_susurrado_hecho]queda dormida con la red que vos y Vega le tejieron[/if][if flag=cap4_sin_ritual]la calman Vega y los Susurradores con un hechizo improvisado que les va a costar dos ciegos más y una enfermedad nueva, pero la calman[/if]. Pipo vive. Eso es más de lo que creías poder pedir.',
      'A la semana, en el consejo final, proponés lo que nadie quería oír: alguien tiene que ir al mundo humano. Alguien tiene que llegar al pueblo grande —al que vos dejaste siete años atrás— y contar lo que está pasando. Conseguir aliados humanos. Denunciar al consorcio en los tribunales que todavía funcionan. El bosque solo no se salva.',
      '—Voy yo —decís, sin adorno—. Me fui del pueblo grande hace siete años. Me debo una vuelta.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'ending_renacimiento', valor: true },
      { kind: 'xp', cantidad: 200 },
    ],
    opciones: [
      {
        id: 'r_partir',
        texto: 'Partir. Con la cara al sur y la sombra atrás.',
        destino: 'ending_renacimiento_2',
        intencion: 'valiente',
      },
    ],
  },
  {
    id: 'ending_renacimiento_2',
    capitulo: 5,
    background: { type: 'ruins', hint: 'Franco saliendo por el camino al pueblo grande' },
    texto: [
      'Te despedís de Pipo con una promesa que no tenés cómo cumplir: "vuelvo". Él te mira como lo miran los carpinchos a las mentiras sinceras: sin juicio, con paciencia.',
      '[if flag=sena_vuelve_con_tebo]Sena te acompaña los primeros dos días. Tebo va arriba de sus hombros. En el punto donde el camino se divide, te da una hoja de abedul pintada de rojo: "Esto te identifica con los nuestros del sur si los cruzás. No van a molestarte". Asentís. Te das la mano.[/if][if noflag=sena_vuelve_con_tebo]Nadie te acompaña. Nadie tiene que. Las despedidas largas son una vanidad de los que no confían en volver.[/if]',
      '[if flag=mara_aliada]Mara —ella sí, inesperadamente— camina al lado tuyo durante medio día. Cuando se separa, te dice: "Si encontrás a Reyes en libertad de vuelta, decímelo. Yo me ocupo." Te reís. No te ríe nunca así, Mara. Se va.[/if]',
      'Volvés al pueblo grande. Las calles están más anchas de lo que te acordabas. Tu vieja casa es ahora un comercio de telas. Al tercer día, escribís la primera de muchas cartas al consejo regional. Las cartas van a tardar años en servir. Van a servir.',
    ],
    opciones: [
      {
        id: 'r_epilogo',
        texto: 'Cerrar la primera carta. Empezar la segunda.',
        destino: 'ending_renacimiento_3',
        intencion: 'neutral',
      },
    ],
  },
  {
    id: 'ending_renacimiento_3',
    capitulo: 5,
    titulo: 'Epílogo — Renacimiento',
    background: { type: 'ruins', hint: 'sala de tribunales, Franco viejo testificando' },
    texto: [
      'Tres años. Reyes procesado por violación territorial y asesinato de nativos no humanos (una figura jurídica que inventaron por el caso tuyo y que cambió la historia legal del país). La "Sociedad Forestal del Sur" disuelta. Sus dueños, presos. El bosque, aunque mordido por los bordes, vuelto a ser bosque.',
      'Vos volvés al Bosque Susurrante dos veces al año. La primera visita te chocás con Pipo más viejo pero entero, y con Náyade todavía más vieja pero entera, y con una villa que se parece a la que conociste pero con casas nuevas y con algunos carpinchos que no habías conocido.',
      'La última visita, a los setenta y un años, no la hacés: te morís en una cama prestada del pueblo grande, con dos gatos que te adoptaron en tu vejez echados sobre las piernas. Una noticia chiquita en un diario dice que falleció "un activista que defendió a criaturas del bosque contra los intereses de una empresa forestal del sur". Nadie más se acuerda. Pipo, que lo supo enseguida, no vuelve a comer por dos semanas.',
      '[whisper]Final: Renacimiento. Ganaste lo que se podía ganar.[/whisper]',
    ],
    final: 'renacimiento',
    opciones: [
      {
        id: 'r_volver',
        texto: 'Volver al menú.',
        destino: 'cap1_intro',
        intencion: 'neutral',
      },
    ],
  },

  // ===========================================================================
  //  3. EL EXILIO VOLUNTARIO
  // ===========================================================================
  {
    id: 'ending_exilio_1',
    capitulo: 5,
    titulo: 'El Exilio Voluntario',
    background: { type: 'forest', hint: 'caravana chica al anochecer, dejando el Corazón atrás' },
    musica: 'ending_neutral',
    texto: [
      'Hablás claro al consejo. —No puedo con todo. No puedo con la piedra, no puedo con Reyes, no puedo con los siete siglos de rencor entre Clan del Río y Guardianes del Fuego. Pero puedo con los que están dispuestos a venir conmigo al norte. A un bosque que, dicen los Susurradores, nadie conoce. Si vienen, vienen. Si no, no los culpo.',
      'Vienen nueve. [if flag=pipo_rescatado]Pipo, obviamente.[/if] [if flag=sena_vuelve_con_tebo]Sena y Tebo, que cumplen la promesa vieja.[/if] Dos familias del Clan del Río. Uno de los Susurradores más jóvenes, que se pelea con Vega y decide probar suerte con vos. Dos crías huérfanas. La de ustedes.',
      'Atrás queda lo que no elegiste. La batalla sigue sin vos. La piedra sigue sin vos. Te vas con el peso de las cosas que no podés sostener.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'ending_exilio', valor: true },
      { kind: 'xp', cantidad: 150 },
    ],
    opciones: [
      {
        id: 'ex_camino',
        texto: 'Caminar. Tres meses de caminar.',
        destino: 'ending_exilio_2',
        intencion: 'neutral',
      },
    ],
  },
  {
    id: 'ending_exilio_2',
    capitulo: 5,
    background: { type: 'mystical', hint: 'bosque nuevo al amanecer, desconocido' },
    texto: [
      'El nuevo bosque es duro. Los primeros dos meses comen raíz y una bayas que Pipo identifica con un instinto que no sabés si le queda de la piedra o de otra cosa. A los tres meses encuentran un claro bueno, con agua. Arman un asentamiento.',
      'El primer invierno se llevan dos. Una cría y un carpincho viejo que venía ya muy gastado. Los entierran en piedra —porque no hay madera seca todavía para hogueras funerarias largas—. A Franco le toca leer las palabras porque Sena no se anima y Pipo no habla.',
      '[if flag=pipo_rescatado]Pipo, acá, empieza a mirar al sur cada amanecer. Vos lo sabés. No decís nada. Algunos amores se respetan con el silencio de no preguntar qué extrañan.[/if]',
      'A los dos años son quince. A los cinco, veintidós. A los diez —a los diez ya no sos vos el que lleva la cuenta— son una aldea con su propio nombre.',
    ],
    opciones: [
      {
        id: 'ex_epilogo',
        texto: 'Dejar pasar los años.',
        destino: 'ending_exilio_3',
        intencion: 'neutral',
      },
    ],
  },
  {
    id: 'ending_exilio_3',
    capitulo: 5,
    titulo: 'Epílogo — El Exilio Voluntario',
    background: { type: 'forest', hint: 'Franco viejo al lado de Pipo viejo, bosque tranquilo' },
    texto: [
      'De lo que dejaste atrás, te enterás tarde. A los seis años llega un mensajero de los Susurradores con una carta de Vega: "La piedra despertó. No pudimos. Reyes volvió con más gente. La Villa resistió nueve meses. Después cayó. Náyade no escapó. El bosque donde viviste ya no es bosque. Te cuento esto porque si no te lo cuento me arrepiento toda la eternidad. Hiciste bien en irte. Las vidas que salvaste son diez vidas. Las que no, no son tu deuda."',
      'Vos no lloras enseguida. Te sentás a la orilla del arroyo nuevo, con Pipo al lado. A la noche llora por fin, cuando Pipo ya se durmió y nadie puede verte.',
      'Pipo muere a los diecinueve años —largo, para un carpincho—, en los brazos tuyos, a la orilla del mismo arroyo. Vos morís a los setenta y cuatro, de viejo, en una casa que te construiste con las manos mientras se te iba enfriando el pecho. La aldea del norte te vela tres días. En la entrada, ponen una piedra con tu nombre y, al lado, una más chica con el de Pipo.',
      '[whisper]Final: El Exilio Voluntario. Salvaste poco. Lo salvaste bien.[/whisper]',
    ],
    final: 'exilio_voluntario',
    opciones: [
      {
        id: 'ex_volver',
        texto: 'Volver al menú.',
        destino: 'cap1_intro',
        intencion: 'neutral',
      },
    ],
  },

  // ===========================================================================
  //  4. LA CORONA DE SANGRE
  // ===========================================================================
  {
    id: 'ending_corona_1',
    capitulo: 5,
    titulo: 'La Corona de Sangre',
    background: { type: 'fire', hint: 'batalla recrudece, Franco gritando órdenes' },
    musica: 'ending_bad',
    texto: [
      'Elegiste la fuerza. No porque fueras cobarde con la diplomacia: porque viste algo en los ojos de Reyes que nunca vas a contarle a nadie. Viste que la gente como él no negocia. Y cerraste la cuenta.',
      'Tomás el comando. Orón te obedece —si sobrevivió— con una rapidez que te sorprende, porque Orón no obedece nunca. Los Guardianes del Fuego se alinean atrás tuyo. Ordenás el contraataque total.',
      'Lo que sigue no es una batalla: es una [emph]liquidación[/emph]. Reyes muere en el cuarto asalto con dos tiros en el cuello. La Sombra disidente, confundida por la situación, cae también: les pegan los Guardianes creyendo que son enemigos, no discutís el error. Los Susurradores se retiran escandalizados, y algunos no se retiran a tiempo.',
      'Cuando cesa el fuego, quedan cuerpos humanos y carpinchos mezclados. Más carpinchos que humanos, proporcionalmente. Vos mirás y no apartás la vista, porque si la apartás sos menos líder de lo que decidiste ser.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'ending_corona_sangre', valor: true },
      { kind: 'xp', cantidad: 150 },
    ],
    opciones: [
      {
        id: 'c_trono',
        texto: 'Subir al centro del Corazón quemado. Declararte lo que nadie te pidió.',
        destino: 'ending_corona_2',
        intencion: 'hostil',
      },
    ],
  },
  {
    id: 'ending_corona_2',
    capitulo: 5,
    background: { type: 'ruins', hint: 'Franco de pie sobre escalones, los demás abajo' },
    texto: [
      'Declarás la unificación forzada. Llamás "Guardián" al nuevo régimen: el nombre que antes era sagrado, ahora es tuyo. Nadie te discute. Nadie tiene ganas.',
      '[if flag=pipo_rescatado]Pipo, en la bandolera, se te despierta. Te mira con los ojos suyos —no blancos— y hay algo en esa mirada que te dice que él sabe que vos perdiste algo que no sabe nombrar. No te chilla. Se te queda mirando con esa paciencia de carpincho que a estas alturas ya es un reproche sin voz.[/if]',
      'La piedra sigue durmiendo —o despierta en sus cámaras—. Ya no te importa tanto como te importaba hace una hora. Las cosas nuevas hacen chiquitas a las antiguas.',
      'Firma tu primera orden el mismo día. Son veinte ejecuciones de humanos de Reyes que se rindieron. Orón te pregunta si estás seguro. Le decís que sí sin mirarlo. Las veinte ejecuciones se cumplen.',
    ],
    opciones: [
      {
        id: 'c_epilogo',
        texto: 'Dejar pasar los años.',
        destino: 'ending_corona_3',
        intencion: 'neutral',
      },
    ],
  },
  {
    id: 'ending_corona_3',
    capitulo: 5,
    titulo: 'Epílogo — La Corona de Sangre',
    background: { type: 'ruins', hint: 'salón vacío con Franco viejo en un sillón' },
    texto: [
      'Gobernás veintitrés años. Eficaz. Brutal cuando hace falta. Tres intentos de asesinato, ninguno exitoso. Dos hijos que la villa te da por decreto —nunca te casás; tener hijos es una formalidad del régimen, no un amor—.',
      'Los incendios paran. El consorcio humano se retira, aterrado por un ejército que no esperaban. La paz que imponés es paz de verdad, si la paz se mide por cantidad de gente viva. Si se mide por otras cosas, no tanto.',
      '[if flag=pipo_rescatado]Pipo muere al séptimo año de tu gobierno, en una cama de tela que vos mismo le acomodaste. Lo enterrás vos mismo al amanecer. Nadie te acompaña, no porque no quieran: porque nadie se anima a acercarse a un Guardián que llora.[/if]',
      'Morís a los sesenta y ocho, en tu sillón, leyendo un informe. Nadie llora en público. Algunos lloran de alivio en privado. Te hacen un funeral largo con los honores que el régimen exige. Pasan veinte años hasta que alguien se anima a decir en voz alta que la paz fue un error.',
      '[whisper]Final: La Corona de Sangre. Ganaste todo. Perdiste algo que no sabés nombrar.[/whisper]',
    ],
    final: 'corona_sangre',
    opciones: [
      {
        id: 'c_volver',
        texto: 'Volver al menú.',
        destino: 'cap1_intro',
        intencion: 'neutral',
      },
    ],
  },

  // ===========================================================================
  //  5. HERMANDAD CON LA SOMBRA
  // ===========================================================================
  {
    id: 'ending_sombra_1',
    capitulo: 5,
    titulo: 'Hermandad con La Sombra',
    background: { type: 'cave', hint: 'Franco caminando hacia el Cuervo en la plaza' },
    musica: 'ending_bad',
    texto: [
      'Caminás hacia el Cuervo. Él te mira sin asombro: sabía, desde el primer día, que podías volver a él. No sonríe. Te abre el brazo izquierdo en un gesto que parece padre.',
      '—Sabías que era este el final —te dice.',
      'No hay negociación. Anunciás a los demás tu decisión de "unir fuerzas" con La Sombra en esta batalla. Náyade, que no lo esperaba, te mira como si estuvieras enfermo. Vega, que sí lo esperaba, no reacciona. Orón —si vino— saca la hoja. Lo paran, con esfuerzo, tres de los suyos.',
      'Vos [if flag=pipo_rescatado]soltás a Pipo. La piedra elige a Pipo, como siempre iba a elegir. Pipo camina a la cámara y entra. La puerta de piedra se cierra sola.[/if][if noflag=pipo_rescatado]mirás al horizonte. En el este, donde sea que esté Pipo, la piedra lo encuentra.[/if]',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'ending_hermandad_sombra', valor: true },
      { kind: 'xp', cantidad: 100 },
    ],
    opciones: [
      {
        id: 's_limpieza',
        texto: 'Ayudar al Cuervo a terminar lo que empezó.',
        destino: 'ending_sombra_2',
        intencion: 'hostil',
      },
    ],
  },
  {
    id: 'ending_sombra_2',
    capitulo: 5,
    background: { type: 'fire', hint: 'bosque ardiendo sistemáticamente, caminos cerrados' },
    texto: [
      'Los meses siguientes son sistemáticos, como dijiste que serían. Con Pipo-piedra operando como arma viva y el Cuervo como líder restaurado, La Sombra barre a las otras facciones una por una.',
      'El Clan del Río es exterminado aldea por aldea. Náyade muere en un asalto nocturno que vos mismo coordinaste. Los Susurradores se resisten más —Vega es difícil de matar—, pero caen. Los Guardianes pelean hasta el último.',
      'Al pueblo grande le va peor. Pipo-piedra no distingue humanos de carpinchos. No distingue enemigos de aliados. La piedra, a estas alturas, ya se comió al pibe: solo queda la cosa usando un cuerpo chico.',
      '[if flag=cuervo_sobrevive]Al año, el Cuervo muere. No matan: se suicida. Te deja una carta de dos palabras: "[emph]Perdoname igual[/emph]". No lo perdonás. Ni vos ni él.[/if]',
    ],
    opciones: [
      {
        id: 's_epilogo',
        texto: 'Seguir solo.',
        destino: 'ending_sombra_3',
        intencion: 'hostil',
      },
    ],
  },
  {
    id: 'ending_sombra_3',
    capitulo: 5,
    titulo: 'Epílogo — Hermandad con La Sombra',
    background: { type: 'fire', hint: 'cielo permanentemente cenizo, Franco solo' },
    texto: [
      'Al tercer año, quedás solo. La Sombra misma —facción de La Sombra, si se puede llamar así a lo que queda— decide al fin que un humano sobra en este bosque. Una noche te cortan el cuello mientras dormís.',
      'No te ven morir. No hay testigos. Tu cuerpo lo entierran en un pozo sin nombre. Al mes, Pipo-piedra colapsa: la cosa terminó de alimentarse y se durmió mil años en la carne del carpincho al que criaste cuatro.',
      'El bosque se queda quieto. Cenizo. Sin vida humana ni carpibárica sapiente. Las plantas vuelven a ser plantas. Los animales comunes —zorros, lechuzas, el carpincho no sapientis— vuelven a lo suyo, y la historia de esta civilización pasa a ser mito mal contado en los pueblos del sur.',
      'En tu tumba sin nombre, a las décadas, crece un árbol. No da fruta. No da sombra. Nada en él te recuerda, porque nada sabe que estás ahí.',
      '[whisper]Final: Hermandad con La Sombra. Hiciste el peor pacto posible con el peor interlocutor posible.[/whisper]',
    ],
    final: 'hermandad_sombra',
    opciones: [
      {
        id: 's_volver',
        texto: 'Volver al menú.',
        destino: 'cap1_intro',
        intencion: 'neutral',
      },
    ],
  },

  // ===========================================================================
  //  6. EL ÚLTIMO FUEGO
  // ===========================================================================
  {
    id: 'ending_ultimo_fuego_1',
    capitulo: 5,
    titulo: 'El último fuego',
    background: { type: 'fire', hint: 'Franco arrodillado mientras todo arde alrededor' },
    musica: 'ending_bad',
    texto: [
      'No hay una palabra para lo que sentís. "Cansancio" es chico. "Rendición" es engañosa porque implica que había pelea. Es otra cosa. Es la certeza exacta de que cada puerta que abriste desde el primer día te llevó a una escalera más baja, y ahora estás en la última escalera y abajo no hay piso.',
      'Abandonaste a Pipo la primera noche. Fallaste el rescate. No completaste el contenedor. No conseguiste aliados sólidos. Llegaste al Corazón con cuatro personas que no son los cuatro que hacían falta. El ejército de Reyes está ganando. La piedra está despertando.',
      'No hay decisión que tomar. Hay que aceptar.',
      'Te sentás en la escalinata de la biblioteca quemada. El fuego ya cruzó la plaza. Te llega al pelo. No te levantás.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'ending_ultimo_fuego', valor: true },
      { kind: 'xp', cantidad: 50 },
    ],
    opciones: [
      {
        id: 'uf_cerrar_ojos',
        texto: 'Cerrar los ojos.',
        destino: 'ending_ultimo_fuego_2',
        intencion: 'neutral',
      },
    ],
  },
  {
    id: 'ending_ultimo_fuego_2',
    capitulo: 5,
    titulo: 'Epílogo — El Último Fuego',
    background: { type: 'fire', hint: 'humo lo cubre todo, silencio' },
    texto: [
      'El humo te encuentra antes que el fuego. Los ojos te arden. No te los limpiás. No hay por qué.',
      'Pensás en tu madre. En una carta que no le respondiste. En una palabra que le dijiste en un mal momento. En la noche a los dieciocho en que tu hermana te dijo "volvé" y vos no volviste.',
      'Pensás en Pipo. No en la última vez que lo viste —que fue huyendo—: en la primera. Una cría ciega que te cabía en una mano. Un olor a leche de monte que te agarró el pecho y no te lo soltó en cuatro años. "Qué tonto", pensás, "lo quise como a un hijo y ni siquiera me di cuenta a tiempo".',
      'El humo sube. Tu conciencia baja. No hay último pensamiento. No hay músicas fúnebres. Hay [shake]silencio[/shake]. Un silencio que se expande hasta cubrir el bosque, el pueblo grande, el país.',
      'Al año siguiente, nadie se acuerda del Bosque Susurrante. Al siguiente, ni siquiera los cartógrafos. Al tercero, la región aparece en los mapas nuevos como "tierra sin interés". No es un castigo cósmico: es lo que pasa cuando un mundo se apaga y nadie le sostiene la mano.',
      '[whisper]Final: El Último Fuego. Fallaste todo. El mundo no se ofendió: apenas se desentendió.[/whisper]',
    ],
    final: 'ultimo_fuego',
    opciones: [
      {
        id: 'uf_volver',
        texto: 'Volver al menú.',
        destino: 'cap1_intro',
        intencion: 'neutral',
      },
    ],
  },
];
